import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import VerifiedAddressSearch from "components/address/VerifiedAddressSearch";
import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";

import { useAuth } from "context/AuthContext";
import { get, put } from "lib/api";
import { isPortalRole } from "lib/portalRoles";
import {
  addressRowKey,
  applyAddressCandidate,
  createAddressRow,
  isAddressRowFilled,
  isVerifiedAddressRow,
  normalizeAddressRow,
  stripAddressRowForSave,
  useStorefrontAddressLookup,
} from "lib/storefrontAddressLookup";

export default function CustomerAddresses() {
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();

  const role = String(pluginRole || "").toLowerCase();
  const isPortal = isPortalRole(role);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [addresses, setAddresses] = useState([]);
  const addressLookup = useStorefrontAddressLookup();
  const { syncRows } = addressLookup;

  useEffect(() => {
    syncRows(addresses, addressRowKey);
  }, [addresses, syncRows]);

  useEffect(() => {
    if (!token || !isPortal) return;
    (async () => {
      setLoading(true);
      setMessage(null);
      try {
        const res = await get("/api/storefront/me");
        const storefrontProfile = res?.profile || {};
        const arr = Array.isArray(storefrontProfile?.addresses) ? storefrontProfile.addresses : [];
        setAddresses(arr.map((address) => normalizeAddressRow(address)));
      } catch (e) {
        const msg = e?.response?.data?.error?.message || e?.message || "Failed to load addresses";
        setMessage({ type: "error", text: msg });
      } finally {
        setLoading(false);
      }
    })();
  }, [token, isPortal]);

  if (!token) return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  if (authLoading || pluginRole == null) return null;
  if (!isPortal) return <Navigate to="/dashboard" replace />;

  const canSave = useMemo(() => {
    return !loading && !saving;
  }, [loading, saving, addresses]);

  const updateAddress = (index, field, value) => {
    setAddresses((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
              ...(field === "street" || field === "city" || field === "state" || field === "postalCode" || field === "country"
                ? { formattedAddress: "", placeId: "" }
                : {}),
            }
          : row
      )
    );
  };

  const setPrimaryAddress = (index) => {
    setAddresses((prev) => prev.map((row, rowIndex) => ({ ...row, isPrimary: rowIndex === index })));
  };

  const applySelectedCandidate = (index, candidate) => {
    const rowKey = addressRowKey(addresses[index], index);
    addressLookup.applyCandidateForRow(rowKey, candidate);
    setAddresses((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        return applyAddressCandidate(row, candidate);
      })
    );
  };

  const onSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const next = (addresses || [])
        .map((address) => stripAddressRowForSave(address))
        .filter((address) => isAddressRowFilled(address));
      if (next.some((address) => !isVerifiedAddressRow(address))) {
        throw new Error("Select a verified address from Google suggestions for every saved address.");
      }

      const primaryAddress = addresses.find((row) => row.isPrimary);

      await put("/api/storefront/me", {
        data: {
          addresses: next,
          ...(primaryAddress?.addressId ? { primaryAddressId: String(primaryAddress.addressId) } : {}),
        },
      });
      setMessage({ type: "success", text: "Addresses saved." });
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.message || "Failed to save addresses";
      setMessage({ type: "error", text: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomerPortalLayout
      title="Addresses"
      subtitle="Saved shipping addresses for checkout."
      actions={
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <MDButton
            variant="outlined"
            color="info"
            startIcon={<Icon>add</Icon>}
            disabled={loading || saving}
            onClick={() =>
              setAddresses((prev) => [
                ...(prev || []),
                createAddressRow(null, { isPrimary: !(prev || []).length }),
              ])
            }
          >
            Add address
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={onSave} disabled={!canSave}>
            {saving ? "Saving…" : "Save"}
          </MDButton>
        </Box>
      }
    >
      {message ? (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      ) : null}

      {loading ? (
        <Alert severity="info">Loading…</Alert>
      ) : !(addresses || []).length ? (
        <Alert severity="info">
          No saved addresses yet. Add one for faster checkout.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {(addresses || []).map((a, idx) => (
            <Card key={addressRowKey(a, idx)} variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={2}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Box>
                    <MDTypography variant="h6" fontWeight={900}>
                      {a.label?.trim() ? a.label : `Address ${idx + 1}`}
                    </MDTypography>
                    <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
                      {a.isPrimary ? "Primary shipping address" : "Saved shipping address"}
                    </MDTypography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {!a.isPrimary ? (
                      <MDButton variant="text" color="info" onClick={() => setPrimaryAddress(idx)} disabled={saving}>
                        Set primary
                      </MDButton>
                    ) : null}
                    <MDButton
                      variant="text"
                      color="error"
                      startIcon={<Icon>delete</Icon>}
                      onClick={() => {
                        addressLookup.clearRow(addressRowKey(a, idx));
                        setAddresses((prev) => (prev || []).filter((_, i) => i !== idx));
                      }}
                      disabled={saving}
                    >
                      Remove
                    </MDButton>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Label"
                      fullWidth
                      value={a.label || ""}
                      onChange={(e) => updateAddress(idx, "label", e.target.value)}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Recipient name"
                      fullWidth
                      value={a.name || ""}
                      onChange={(e) => updateAddress(idx, "name", e.target.value)}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <VerifiedAddressSearch
                      rowKey={addressRowKey(a, idx)}
                      address={a}
                      lookup={addressLookup}
                      disabled={saving}
                      label="Find address or business"
                      helperText="Choose the exact Google place, then confirm the pin on the map."
                      onTyping={() => {
                        updateAddress(idx, "formattedAddress", "");
                        updateAddress(idx, "placeId", "");
                      }}
                      onSelect={(option) => applySelectedCandidate(idx, option)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Address email"
                      fullWidth
                      value={a.email || ""}
                      onChange={(e) => updateAddress(idx, "email", e.target.value)}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Address phone"
                      fullWidth
                      value={a.phone || ""}
                      onChange={(e) => updateAddress(idx, "phone", e.target.value)}
                      disabled={saving}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}
    </CustomerPortalLayout>
  );
}
